import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Gift, MapPin, Truck, X, Check, Loader2, AlertCircle } from 'lucide-react'
import { giftsApi, hospitalsApi, exchangesApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'
import { sanitizeHtml } from '../../lib/sanitize'

const GiftDetail = () => {
  usePageTitle()
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState<any>(null)
  const [gift, setGift] = useState<any>(null)
  const [hospitals, setHospitals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false)
  const [deliveryType, setDeliveryType] = useState<'express' | 'selfpickup'>('selfpickup')
  const [receiverName, setReceiverName] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [showGiftStock, setShowGiftStock] = useState(true)
  const [allowExchange, setAllowExchange] = useState(true)
  const [minPointsWithdraw, setMinPointsWithdraw] = useState(0)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showIdCardModal, setShowIdCardModal] = useState(false)
  const [successInfo, setSuccessInfo] = useState({ deliveryType: '', hospital: '', receiverName: '', address: '', phone: '' })

  useEffect(() => {
    window.scrollTo(0, 0)
    // showGiftStock 单独存储
    const showStock = localStorage.getItem('showGiftStock')
    if (showStock !== null) setShowGiftStock(showStock === 'true')
    // allowExchange 单独存储
    const allowEx = localStorage.getItem('allowExchange')
    if (allowEx !== null) setAllowExchange(allowEx === 'true')
    // minPointsWithdraw 在 systemSettings 中
    const saved = localStorage.getItem('systemSettings')
    if (saved) {
      const parsed = JSON.parse(saved)
      if (parsed.minPointsWithdraw !== undefined) setMinPointsWithdraw(Number(parsed.minPointsWithdraw) || 0)
    }
    const stored = localStorage.getItem('userAccount')
    if (stored) {
      try { setUser(JSON.parse(stored)) } catch { /* ignore */ }
    }

    const loadData = async () => {
      if (!id) return
      try {
        const [giftResp, hospResp] = await Promise.all([
          giftsApi.detail(id),
          hospitalsApi.list(),
        ]) as [any, any]
        setGift(giftResp)
        setHospitals(hospResp.hospitals || hospResp || [])
        // 默认配送方式
        if (giftResp.delivery_type === 'express') setDeliveryType('express')
        else if (giftResp.delivery_type === 'selfpickup') setDeliveryType('selfpickup')
        else setDeliveryType('selfpickup')
      } catch (e) {
        console.error('Failed to load gift detail', e)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600 mb-3" />
        <p className="text-gray-500">加载中...</p>
      </div>
    )
  }

  if (!gift) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="text-gray-500 mb-4">礼品不存在</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-green-700 text-white rounded-xl">
          返回上一页
        </button>
      </div>
    )
  }

  const userPoints = user?.points ?? 0
  const giftPoints = gift.points || 0
  const hasEnoughPoints = userPoints >= giftPoints
  const hasMinPoints = userPoints >= minPointsWithdraw
  const hasStock = (gift.stock || 0) > 0
  const canExchange = allowExchange && hasEnoughPoints && hasMinPoints && hasStock
  const deliveryTypeVal = gift.delivery_type || 'both'

  const getStockText = () => {
    if (gift.stock === 0) return '已兑完'
    if (gift.stock < 5) return `仅剩${gift.stock}件`
    return `库存${gift.stock}件`
  }

  const handleExchange = () => {
    setError('')
    // 检查身份证号是否已配置
    if (!user?.idCard) {
      setShowIdCardModal(true)
      return
    }
    if (deliveryTypeVal === 'both') {
      setShowDeliveryModal(true)
    } else if (deliveryTypeVal === 'express') {
      setDeliveryType('express')
      setShowDeliveryModal(true)
    } else {
      setDeliveryType('selfpickup')
      setShowDeliveryModal(true)
    }
  }

  const handleConfirm = async () => {
    setError('')
    if (deliveryType === 'express') {
      if (!receiverName || !address || !phone) { setError('请填写完整的收货信息'); return }
      if (!/^1[3-9]\d{9}$/.test(phone)) { setError('请输入正确的手机号'); return }
    }

    setSubmitting(true)
    try {
      await exchangesApi.create({
        gift_id: gift.id,
        hospital_id: user?.hospitalId || '',
        points: gift.points,
        delivery_type: deliveryType,
        receiver_name: deliveryType === 'express' ? receiverName : undefined,
        address: deliveryType === 'express' ? address : undefined,
        phone: deliveryType === 'express' ? phone : undefined,
      })

      const deliveryText = deliveryType === 'express' ? '快递配送' : '医院自提'
      const hospital = deliveryType === 'selfpickup' ? (user?.hospitalName || '') : ''

      setSuccessInfo({ deliveryType: deliveryText, hospital, receiverName, address, phone })
      setShowDeliveryModal(false)
      setShowSuccessModal(true)
    } catch (e: any) {
      setError(e.message || '兑换失败，请重试')
    } finally {
      setSubmitting(false)
    }
  }

  // 获取用户绑定机构信息
  const userHospitalInfo = hospitals.find(h => h.id === user?.hospitalId)

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* 礼品图片 */}
      <div className="relative bg-gray-100">
        <div className="aspect-square">
          {gift.image ? (
            <img src={gift.image} alt={gift.name} className="w-full h-full object-cover absolute inset-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }} />
          ) : null}
          <div className={`absolute inset-0 flex items-center justify-center bg-gray-100 ${gift.image ? 'hidden' : ''}`}>
            <Gift className="w-20 h-20 text-gray-300" />
          </div>
        </div>
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
            {gift.category || '其他'}
          </span>
        </div>
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
          {deliveryTypeVal === 'express' && (
            <span className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs font-medium flex items-center">
              <Truck className="w-3 h-3 mr-1" /> 快递
            </span>
          )}
          {deliveryTypeVal === 'selfpickup' && (
            <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium flex items-center">
              <MapPin className="w-3 h-3 mr-1" /> 自提
            </span>
          )}
          {deliveryTypeVal === 'both' && (
            <span className="px-3 py-1 bg-purple-500 text-white rounded-full text-xs font-medium">快递/自提</span>
          )}
        </div>
      </div>

      {/* 礼品信息 */}
      <div className="px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <h1 className="text-xl font-bold text-gray-800">{gift.name}</h1>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center flex-wrap gap-2">
              <div className="flex items-center">
                <span className="text-2xl font-bold text-orange-500">{gift.points}</span>
                <span className="text-sm text-gray-500 ml-1">积分</span>
              </div>
              {gift.price > 0 && (
                <div className="flex items-center">
                  <span className="text-sm text-gray-400 line-through">市场价 ¥{gift.price}</span>
                </div>
              )}
            </div>
            {showGiftStock && (
              <span className={`text-sm ${gift.stock === 0 ? 'text-red-500' : gift.stock < 5 ? 'text-orange-500' : 'text-green-600'}`}>
                {getStockText()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 详细介绍 */}
      {gift.description && (
        <div className="px-4 mt-4">
          <div className="bg-white rounded-2xl shadow-md p-5">
            <h2 className="font-bold text-gray-800 mb-3">礼品介绍</h2>
            <div
              className="content-body prose prose-sm max-w-none text-sm text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(gift.description) }}
            />
          </div>
        </div>
      )}

      {/* 领取方式提示 */}
      <div className="px-4 mt-4">
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-sm text-gray-500">
            {deliveryTypeVal === 'both' ? '支持快递配送和医院自提，点击兑换后选择领取方式'
              : deliveryTypeVal === 'express' ? '仅支持快递配送，点击兑换后填写收货信息'
              : '仅支持医院自提，点击兑换后确认领取信息'}
          </p>
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 z-20">
        <div className="max-w-lg mx-auto">
          <button onClick={handleExchange} disabled={!canExchange}
            className={`w-full py-4 rounded-2xl font-bold text-lg shadow-lg transition-all ${
              canExchange
                ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            {!allowExchange ? '暂停兑换'
              : !hasStock ? '已兑完'
              : !hasMinPoints ? `积分余额最低需要${minPointsWithdraw}积分才能兑换`
              : !hasEnoughPoints ? '积分不足'
              : '立即兑换'}
          </button>
          <p className="text-center text-xs text-gray-400 mt-2">当前可用积分：{userPoints}分</p>
        </div>
      </div>

      {/* 领取方式弹窗 */}
      {showDeliveryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">选择领取方式</h3>
              <button onClick={() => setShowDeliveryModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {deliveryTypeVal === 'both' && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button onClick={() => setDeliveryType('selfpickup')}
                  className={`p-3 rounded-xl border-2 transition-all ${deliveryType === 'selfpickup' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}>
                  <MapPin className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'selfpickup' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className={`text-xs font-medium ${deliveryType === 'selfpickup' ? 'text-green-700' : 'text-gray-700'}`}>医院自提</p>
                </button>
                <button onClick={() => setDeliveryType('express')}
                  className={`p-3 rounded-xl border-2 transition-all ${deliveryType === 'express' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}>
                  <Truck className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'express' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className={`text-xs font-medium ${deliveryType === 'express' ? 'text-green-700' : 'text-gray-700'}`}>快递配送</p>
                </button>
              </div>
            )}

            {deliveryType === 'selfpickup' && (
              <div className="mb-4 p-4 bg-green-50 rounded-xl">
                <p className="text-sm font-medium text-green-800 mb-2">领取地点</p>
                <p className="text-green-700 font-medium">{user?.hospitalName || '未绑定机构'}</p>
                {userHospitalInfo && (
                  <>
                    <p className="text-sm text-green-600 mt-2">地址：{userHospitalInfo.address || '-'}</p>
                    <p className="text-sm text-green-600">电话：{userHospitalInfo.phone || '-'}</p>
                    {userHospitalInfo.remark && (
                      <p className="text-sm text-orange-600 mt-2 font-medium">领取说明：{userHospitalInfo.remark}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {deliveryType === 'express' && (
              <div className="mb-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">收件人</label>
                  <input type="text" value={receiverName} onChange={(e) => setReceiverName(e.target.value)}
                    placeholder="请输入收件人姓名"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">收货地址</label>
                  <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
                    placeholder="请输入详细收货地址"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">联系电话</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="请输入手机号" maxLength={11}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
            )}

            {error && (
              <div className="mb-3 p-3 bg-red-50 rounded-xl flex items-center text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            <button onClick={handleConfirm} disabled={submitting}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center ${
                submitting ? 'bg-gray-300 text-gray-500' : 'bg-green-700 text-white hover:bg-green-800'
              }`}>
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />兑换中...</> : `确认兑换 (-${gift.points}积分)`}
            </button>
          </div>
        </div>
      )}

      {/* 身份证未设置弹窗 */}
      {showIdCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">提示</h3>
              <button onClick={() => setShowIdCardModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-600 mb-6">暂未设置身份证号，不支持兑换</p>
              <button onClick={() => { setShowIdCardModal(false); navigate('/user/personal-info') }}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700">
                立即设置
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 兑换成功弹窗 */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">兑换成功</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>配送方式：{successInfo.deliveryType}</p>
                {successInfo.deliveryType === '医院自提' ? (
                  <p>领取医院：{successInfo.hospital}</p>
                ) : (
                  <>
                    <p>收件人：{successInfo.receiverName}</p>
                    <p>收货地址：{successInfo.address}</p>
                    <p>联系电话：{successInfo.phone}</p>
                  </>
                )}
              </div>
            </div>
            <div className="border-t border-gray-100">
              <button onClick={() => { setShowSuccessModal(false); navigate('/user/exchange-records') }}
                className="w-full py-3 text-green-700 font-medium hover:bg-green-50 transition-colors">
                查看兑换记录
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GiftDetail
